import { readInventoryField } from './inventory-raw-readers';
import { readItemField } from './item-field-reader';
import { fromBaseRecord, fromBaseUuid, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';

/** Preserve the native SELECT's collation order. Held rows bind it to the same
 * source snapshot; JS must not approximate MySQL's accent/punctuation weights. */
export function createFromBaseInventoryOrder(nodes: ReadonlyMap<string, Row>, orderedRows: readonly Row[],
    template: (id: string) => Row | null): (parent: string) => string[] {
    const seen = new Set<string>(), children = new Map<string, string[]>();
    for (const row of orderedRows) {
        if (!fromBaseRecord(row)) throw new TypeError('Invalid inventory order source');
        const source = fromBaseRecord(row.inventory) ? row : { inventory: row };
        const value = readInventoryField(source, 'inventory_id');
        if (!fromBaseUuid(value)) throw new TypeError('Invalid ordered inventory identity');
        const id = value.toUpperCase(), node = nodes.get(id);
        if (!node || seen.has(id)) throw new TypeError(`Unknown or duplicate inventory order identity: ${id}`);
        seen.add(id);
        for (const field of ['inventory_item_id', 'possess_id', 'inventory_name']) {
            if (readInventoryField(source, field) !== readInventoryField(node, field))
                throw new FromBaseStageMissing('database-collation-order', `stale-source:${id}:${field}`);
        }
        const saved = readInventoryField(node, 'inventory_name');
        if (saved === null || saved === '') {
            const expected = readItemField(template(String(readInventoryField(node, 'inventory_item_id'))), 'item_name');
            if (readItemField(row, 'item_name') !== expected)
                throw new FromBaseStageMissing('database-collation-order', `stale-source:${id}:item_name`);
        }
        const parent = String(readInventoryField(source, 'possess_id')).toUpperCase();
        const siblings = children.get(parent) ?? []; siblings.push(id); children.set(parent, siblings);
    }
    if (seen.size !== nodes.size) throw new FromBaseStageMissing('database-collation-order', 'incomplete-source');
    return parent => [...children.get(parent.toUpperCase()) ?? []];
}
